import React, { useState } from "react";
import { ArrowRight, Loader2, Rocket } from "lucide-react";
import { promoteAll } from "../../lib/uploadsApi";
import { Modal } from "../apikeys/shared";
import { Chip } from "../uploads/FileUploadSource";
import { num } from "../pipeline/Layer";

const ST = { ready: ["Ready", "success"], limited: ["Usable with limits", "warning"], not_ready: ["Not ready", "critical"] };
const S = ({ s }) => <Chip tone={ST[s][1]}>{ST[s][0]}</Chip>;

const Row = ({ f, testid, children }) => (
  <li className="text-[12px] py-1.5 border-b border-border/50 last:border-0" data-testid={testid}>
    <div className="flex flex-wrap items-baseline gap-x-2"><span className="font-mono-tabular">{f.filename}</span>
      <span className="text-muted">{f.label} · {num(f.rows_total)} rows{f.status === "draft" ? " · not reviewed yet" : ""}</span></div>
    {children}
  </li>
);

// Confirmation for Promote all: what will be promoted, what is skipped (blocking findings), and the expected change.
export default function PromoteAllDialog({ m, files, onClose, onStarted }) {
  const pick = m.files.map(id => files[id]).filter(Boolean);
  const go = pick.filter(f => !f.blockers?.length);
  const skip = pick.filter(f => f.blockers?.length);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const confirm = async () => {
    setBusy(true); setErr(null);
    try { onStarted(await promoteAll(m.id, m.files, note.trim())); }
    catch (e) { setErr(e.message); setBusy(false); }
  };
  return (
    <Modal title={`Promote all for ${m.label}`} subtitle="Adds rows to Silver — nothing already there is replaced" icon={Rocket} onClose={onClose} busy={busy} testid="rd-promote-all-dialog">
      <div className="p-5 space-y-4">
        <div className="flex flex-wrap items-center gap-2 text-[12.5px]">Expected: <S s={m.status} /><ArrowRight size={12} className="text-muted" /><S s={m.status_if} /></div>
        <div><div className="text-[10px] uppercase tracking-widest text-muted font-semibold mb-1">Will be promoted · {go.length}</div>
          <ul data-testid="rd-promote-all-go">{go.map(f => <Row key={f.id} f={f} testid={`rd-promote-all-go-${f.id}`} />)}</ul></div>
        {skip.length > 0 && <div><div className="text-[10px] uppercase tracking-widest text-warning font-semibold mb-1">Skipped — fix blockers first · {skip.length}</div>
          <ul data-testid="rd-promote-all-skip">{skip.map(f => <Row key={f.id} f={f} testid={`rd-promote-all-skip-${f.id}`}><div className="text-[11.5px] text-warning">{f.blockers.slice(0, 2).join("; ")}</div></Row>)}</ul></div>}
        <p className="text-[11.5px] text-muted">Files are promoted one by one with their own cleaning rules, including any not reviewed yet. The full files are checked again, so a blocker found outside a review sample can still stop one — the others carry on. Each promotion is logged in the data history.</p>
        <label className="block text-[12px]">Note for the history (optional)
          <input data-testid="rd-promote-all-note" value={note} maxLength={300} onChange={e => setNote(e.target.value)}
                 className="mt-1 w-full h-9 bg-surface border border-border rounded-sm px-2.5 focus:outline-none focus:border-primary" /></label>
        {err && <div className="text-critical text-[12px]" data-testid="rd-promote-all-error">{err}</div>}
        <div className="flex justify-end gap-2">
          <button data-testid="rd-promote-all-cancel" onClick={onClose} disabled={busy} className="h-9 px-4 rounded-sm border border-border text-[12.5px] hover:bg-surface-2 disabled:opacity-45">Cancel</button>
          <button data-testid="rd-promote-all-confirm" onClick={confirm} disabled={busy || !go.length}
                  className="inline-flex items-center gap-1.5 h-9 px-4 rounded-sm bg-primary text-primary-fg text-[12.5px] font-semibold hover:opacity-90 disabled:opacity-45">
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Rocket size={13} />}Promote {go.length} file{go.length === 1 ? "" : "s"}</button>
        </div>
      </div>
    </Modal>
  );
}
